import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { initializeGTM } from './utils/gtm';
import { BRAND } from './branding';

// Brand the tab title per instance (index.html ships the GreenShield default).
document.title = BRAND.documentTitle;

// Only enable Sentry in production or if explicitly enabled
const isProduction = import.meta.env.MODE === 'production';
const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true' || isProduction;

if (!sentryEnabled) {
//   console.log('[Sentry] Disabled in development mode. Set REACT_APP_SENTRY_ENABLED=true to enable.');
}

// GTM is deferred below (after root.render) to avoid blocking LCP

// Set by the vite:preloadError listener below to the moment it self-heals a
// stale-chunk failure with a reload; beforeSend uses it to drop that noise
// from Sentry, but only for a short window (see comment there). Deliberately
// NOT set when the listener declines to reload (cooldown or attempt budget
// exhausted) — those cases must always report.
let preloadReloadTriggeredAt: number | null = null;
// How long after a self-healing reload we still treat a Sentry event as
// residue of that same reload. Bounded, not permanent: if the navigation
// never actually commits (reload coalesced by the browser, iOS pagehide),
// events after this window report normally instead of the tab going silent
// for the rest of its life.
const PRELOAD_RELOAD_MUTE_WINDOW_MS = 10_000;

// Defer Sentry initialization to after first paint to improve LCP.
// Sentry.ErrorBoundary works as a pass-through until init() is called.
const initSentry = () => {
  Sentry.init({
    enabled: sentryEnabled,
    dsn: sentryEnabled ? (import.meta.env.VITE_SENTRY_DSN || 'https://24d43f4ad6db1a971ac906aac98ed04a@o422586.ingest.us.sentry.io/4509951782092800') : undefined,

    // Environment configuration
    // VITE_SENTRY_ENVIRONMENT distinguishes deployments (e.g. "uat" vs
    // "production") — MODE is "production" for every deployed build, which
    // made UAT and prod events indistinguishable in Sentry.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development',

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content, but keep media playback
        maskAllText: true,
        blockAllMedia: false,
      }),
      // Send console.warn and console.error calls as logs to Sentry.
      // NOTE: 'log' is intentionally excluded — shipping every console.log as a
      // Sentry log envelope flooded the ingest quota and caused HTTP 429s, which
      // silently dropped real error/warn telemetry. The codebase has ~1400
      // console.log calls; only warn/error are worth ingesting.
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Tracing configuration
    tracesSampleRate: 0.1,

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // Note: Exclude port 9100 (Zebra Browser Print) to avoid CORS issues with sentry-trace header
    tracePropagationTargets: [
      /^https?:\/\/localhost:3000/,   // Backend API
      /^https?:\/\/localhost:3021/,   // Frontend dev server
      /^https:\/\/.*\.pharmaguide\.com/,
      /^https:\/\/.*\.greenshieldpharmacy\.com/,
      /^https:\/\/.*\.phox\.app/,     // Production domain
    ],

    // Browser Profiling configuration
    // profilesSampleRate is relative to tracesSampleRate (0.1 * 1.0 = 10%)
    profilesSampleRate: 1.0,

    // Replay sample rates - only capture replays on errors
    replaysSessionSampleRate: 0, // Don't capture session replays
    replaysOnErrorSampleRate: 1.0, // Capture all error replays

    // Release tracking
    release: import.meta.env.VITE_VERSION,

    // Send default PII data (includes IP addresses, user context)
    // WARNING: Be careful with HIPAA compliance - only enable if needed
    sendDefaultPii: true, // Enabled to capture IP addresses and user context

    // Before send hook to filter sensitive data
    beforeSend(event, hint) {
      // Stale-deploy chunk load failure that the vite:preloadError listener below
      // already self-healed with a reload — not worth reporting. Any Sentry event
      // raised during this short window after the reload was triggered is DISCARDED
      // here, not deferred: a dropped beforeSend event never reaches Sentry. That can
      // occasionally swallow an unrelated error that happens to fire in the same
      // instant (a PATCH resolving, a queue refetch rejecting) as the page navigates
      // away — accepted as a low-volume tradeoff, since the page is unloading anyway.
      // There's no reliable way to narrow this to only chunk-load exceptions without
      // matching on message text, which is worded differently per browser (Chrome,
      // WebKit and Firefox each phrase a preload failure differently), so the whole
      // window is muted rather than building a filter that silently stops working the
      // next time a browser changes its wording. The window itself is bounded, not
      // permanent: if the reload navigation never actually commits (coalesced by the
      // browser, iOS pagehide), events after PRELOAD_RELOAD_MUTE_WINDOW_MS report
      // normally instead of muting the tab for the rest of its life.
      if (
        preloadReloadTriggeredAt !== null &&
        Date.now() - preloadReloadTriggeredAt < PRELOAD_RELOAD_MUTE_WINDOW_MS
      ) {
        return null;
      }

      // Filter out sensitive data from errors
      if (event.request) {
        // Remove authorization headers
        if (event.request.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
        }
      }

      // Don't send authentication errors to Sentry
      if (event.exception?.values?.[0]?.value?.includes('401')) {
        return null;
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      // MUI MenuList race condition: keyboard navigation fires after dropdown unmounts
      "Cannot read properties of null (reading 'firstChild')",
      // Same MUI focus-management race, other symptom: focus() on an unmounted
      // node (Sentry PHOX-GSC-PHASE2-1P / -2Z — 1-2 events/month, no user impact)
      "Cannot read properties of null (reading 'focus')",
    ],

    // Configure breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Filter out sensitive breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }

      // Sanitize data in breadcrumbs
      if (breadcrumb.data) {
        // Remove sensitive fields
        const sensitiveFields = ['password', 'token', 'authorization', 'ssn', 'dob', 'patientId'];
        sensitiveFields.forEach(field => {
          if (breadcrumb.data && breadcrumb.data[field]) {
            breadcrumb.data[field] = '[REDACTED]';
          }
        });
      }

      return breadcrumb;
    },
  });
};

// After a redeploy the browser may hold a stale index.html that references old hashed
// asset filenames; Vite's lazy-chunk preload then rejects (PHOX-GSC-PHASE2-3J / FE-4N).
// Reload to pick up the new build. This is a COOLDOWN guard, not a one-shot: a tab that
// outlives multiple deploys (e.g. a phone left open for weeks) must get a fresh recovery
// attempt each time, not just once for the tab's lifetime. The cooldown still stops a
// tight reload loop if the stale chunk keeps failing after a reload.
const PRELOAD_RELOAD_KEY = 'vite-preload-reloaded-at';
const PRELOAD_RELOAD_COOLDOWN_MS = 30_000;
// Hard ceiling on top of the cooldown: a persistently unavailable chunk (CDN 404, an
// index.html pinned by a long cache TTL) could otherwise reload the tab out from under
// an operator every 30s forever, discarding in-progress work elsewhere on the page.
// App.tsx deliberately does NOT auto-reload for version changes for the same reason
// (App.tsx:70-73, useVersionCheck's dismissible snackbar) — unbounded auto-reloading
// here would contradict that. Once the budget is spent, stop reloading and let the
// error surface to the ErrorBoundary and Sentry instead of looping.
const PRELOAD_RELOAD_COUNT_KEY = 'vite-preload-reload-count';
const PRELOAD_RELOAD_MAX_ATTEMPTS = 3;
// A gap this much longer than the cooldown means the earlier reload's recovery evidently
// held (the page has been running healthily since) — this is a fresh failure, most likely
// a new deploy, not a continuation of the same failure run. Reset the attempt budget when
// that gap is seen so a tab that survives many deploys keeps getting a fresh recovery
// attempt each time, while a tight loop of failures (all closer together than this) stays
// capped at PRELOAD_RELOAD_MAX_ATTEMPTS.
const PRELOAD_RELOAD_BUDGET_RESET_MS = 5 * 60_000;

// In-memory fallback for the timestamp/attempt-count pair normally kept in sessionStorage.
// Used whenever sessionStorage is unavailable (blocked/partitioned storage, e.g. iOS
// private browsing) so the cooldown and attempt budget still bound this page's lifetime
// instead of never engaging. It can't survive an actual navigation the way sessionStorage
// does — a full reload wipes module state — so a storage-blocked browser only gets
// per-page-lifetime bounding, not cross-reload bounding. That's a deliberate degradation,
// not a gap: bursts of events firing before the browser's reload actually takes effect
// (the reload call is not synchronous) are exactly what used to cause unbounded reloads.
let memoryLastReloadAt = 0;
let memoryReloadAttempts = 0;

// The old one-shot flag this cooldown+budget guard replaced. Best-effort cleanup so it
// doesn't linger indefinitely in sessions that set it before this change shipped.
try {
  sessionStorage.removeItem('vite-preload-reloaded');
} catch {
  // sessionStorage blocked — nothing to clean up.
}

window.addEventListener('vite:preloadError', () => {
  let last = 0;
  let attempts = 0;
  let storageOk = true;
  try {
    last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0;
    attempts = Number(sessionStorage.getItem(PRELOAD_RELOAD_COUNT_KEY)) || 0;
  } catch {
    // sessionStorage blocked (e.g. iOS private browsing) — fall back to the in-memory
    // counters so the cooldown/budget still bound this page's lifetime.
    storageOk = false;
    last = memoryLastReloadAt;
    attempts = memoryReloadAttempts;
  }

  // A negative diff means the system clock moved backwards since the last attempt —
  // treat the cooldown as elapsed rather than let a clock glitch permanently block
  // recovery.
  const sinceLast = Date.now() - last;

  if (last > 0 && sinceLast > PRELOAD_RELOAD_BUDGET_RESET_MS) {
    attempts = 0; // earlier run's recovery evidently held — fresh budget for this failure
  }

  const withinCooldown = last > 0 && sinceLast >= 0 && sinceLast < PRELOAD_RELOAD_COOLDOWN_MS;
  if (withinCooldown) return; // genuine loop — let it surface
  if (attempts >= PRELOAD_RELOAD_MAX_ATTEMPTS) return; // budget spent — let it surface

  const now = Date.now();
  if (storageOk) {
    try {
      sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(now));
      sessionStorage.setItem(PRELOAD_RELOAD_COUNT_KEY, String(attempts + 1));
    } catch {
      // sessionStorage became unavailable between read and write — fall back to memory
      // below so the reload we're about to do still counts against the budget.
      storageOk = false;
    }
  }
  if (!storageOk) {
    memoryLastReloadAt = now;
    memoryReloadAttempts = attempts + 1;
  }

  preloadReloadTriggeredAt = now;
  window.location.reload();
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>An error occurred</h2>
          <p>We've been notified and are working on a fix.</p>
          <button onClick={resetError}>Try again</button>
        </div>
      )}
      showDialog
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// Initialize Sentry and GTM after first paint so they don't block LCP
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(initSentry);
  requestIdleCallback(initializeGTM);
} else {
  setTimeout(initSentry, 0);
  setTimeout(initializeGTM, 0);
}
